// ─────────────────────────────────────────────────────────────────────────────
// Formalização rápida — o caminho curto.
//
// A formalização tradicional é um fluxo de três fases amarrado a um ticket:
// busca no MySuite, tipo de atendimento, classificação, anexos, envio da
// interação. É o certo quando existe atendimento para registrar.
//
// Nem sempre existe. Às vezes o operador só quer o texto que ele escreveu
// passado a limpo — para colar num e-mail, num chat, numa observação. Para
// isso, todo o resto é atrito.
//
// Esta é a alternativa: entra texto, sai texto formalizado. Não toca em ticket
// nenhum, não classifica, não registra interação, não grava histórico. Uma
// webhook própria (`formalizador-nexus`), com prompt próprio.
// ─────────────────────────────────────────────────────────────────────────────

import { WEBHOOKS } from '../config/ambiente';

/**
 * Texto formalizado de dentro da resposta do n8n.
 *
 * Os fluxos do Orion não respondem todos no mesmo formato — uns devolvem
 * `ticket_texto`, outros `output`, outros um array com um item só, e há os que
 * respondem texto puro. Aceitar todos aqui evita que uma mudança de nó no n8n
 * quebre a tela.
 */
function textoDaResposta(dados) {
  const r = Array.isArray(dados) ? dados[0] : dados;
  if (r == null) return '';
  const bruto = (typeof r === 'string')
    ? r
    : (r.ticket_texto || r.texto_final || r.ticket || r.texto || r.text || r.output || r.resposta || '');
  // O fluxo às vezes devolve o texto em HTML simples: <br> vira quebra de linha
  // de verdade, senão o operador cola a tag junto.
  return String(bruto).replace(/<br\s*\/?>/gi, '\n').trim();
}

/**
 * Manda o texto para a webhook de formalização rápida e devolve o formalizado.
 *
 * `operador` entra só para o fluxo saber quem pediu (registro do lado do n8n);
 * nada no retorno depende dele.
 *
 * Lança quando a webhook falha ou responde vazio — quem chama mostra o aviso.
 */
export async function formalizarRapido(texto, operador = null) {
  const conteudo = String(texto || '').trim();
  if (!conteudo) throw new Error('Escreva o texto que deve ser formalizado.');

  const res = await fetch(WEBHOOKS.formalizadorRapido, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      texto: conteudo,
      // Mesmo nome de campo usado pelos outros fluxos do helpdesk, para o n8n
      // poder reaproveitar nós de prompt sem renomear nada.
      anotacoes: conteudo,
      operador_nome: operador?.nome || '',
      operador_email: operador?.email || '',
      origem: 'orion-formalizacao-rapida',
    }),
  });

  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    try { const e = await res.json(); msg = e.message || e.error || msg; } catch (err) { /* corpo sem JSON */ }
    throw new Error(msg);
  }

  // Resposta pode vir como JSON ou como texto puro, conforme o nó final do n8n.
  const cru = await res.text();
  let dados;
  try { dados = JSON.parse(cru); } catch (e) { dados = cru; }

  const formalizado = textoDaResposta(dados);
  if (!formalizado) throw new Error('O fluxo respondeu sem texto.');
  return formalizado;
}
